/**
 * CLI handlers for `canary workflow cache {status,clear,optimize,substrate,locator,commit}`.
 *
 * @module WorkflowCacheCLI
 */

import process from 'node:process';
import fs from 'node:fs/promises';
import path from 'node:path';
import { getArgValue, hasFlag } from './auth.js';
import { apiRequest } from './cli-helpers.js';
import type { RunData } from '@chatsdet/diagnostic-workspace';
import {
  parseTraceActions,
  parseTraceContext,
  parseTraceGroups,
  type TraceAction,
  type TraceSnapshotPhase,
  type TraceViewport,
} from '@chatsdet/trace-tools';
import type { AssertionSpec, PlaybookStep } from '@chatsdet/types';
import {
  HeadlessDomSession,
  LocatorVerifier,
  SubstrateMaterializer,
  attachRunCodeCarryShadowCandidates,
  authorLinkedArtifacts,
  buildCachePlaybook,
  buildCarryThroughActions,
  classifyPendingRowCandidateDiscovery,
  collectTokens,
  composeDeterministicPlaybookPlan,
  decidePendingRowCandidateOutcomes,
  discoverTarget,
  enrichPlaybookStepsWithRunCode,
  linkSignalsByStepMap,
  linkerMacroResolutionLog,
  optimizeNodeStepsPure,
  prepareDeterministicNodeInputs,
  resolveSegmentNodeIds,
  segmentTraceByNode,
  serializeNodeDecisionLog,
  timelineActionCode,
  traceActionWithPreview,
  rowStrategyForStep,
  type DeterministicCacheRowLike,
  type LinkReasonCode,
  type LinkResult,
  type MacroResolutionLog,
  type NodeDecisionLog,
  type NodeSegment,
  type PendingRowCandidateOutcome,
  type RunCodeToolCall,
  type TimelineCacheCodeEvidence,
} from '@chatsdet/workflow-cache';
import {
  buildOptimizeDryTotals,
  formatOptimizeDrySummary,
  type OptimizeDryNodeSummary,
  type OptimizeDrySummary,
} from './workflow-cache-optimize-dry-format.js';
import {
  parseOptimizeDryArgs,
  type OptimizeDryParsedArgs,
} from './workflow-cache-optimize-dry-args.js';
import {
  assertReadableTrace,
  ensureOptimizeDryWorkspace,
  readJsonlFile,
  type DryRunData,
} from './workflow-cache-optimize-dry-workspace.js';
import { buildCacheClearBody, handleClear } from './workflow-cache-clear.js';
import { collectLocalRunProvenance } from './workflow-cache-optimize-dry-provenance.js';

export { buildOptimizeDryTotals, formatOptimizeDrySummary };
export type { OptimizeDryNodeSummary, OptimizeDrySummary };

export { enrichPlaybookStepsWithRunCode, timelineActionCode };
export type { TimelineCacheCodeEvidence };
export { parseOptimizeDryArgs };
export type { OptimizeDryParsedArgs };
export { buildCacheClearBody };

type CacheStatusResponse = {
  ok: boolean;
  error?: string;
  workflowId?: string;
  nodes?: Array<{
    nodeId: string;
    cacheId: string;
    isValid?: boolean;
    compilerVersion: number | null;
    playbookHealth: string | null;
    stepFailureCounts: Record<string, number> | null;
    cachedAt: string | null;
    lastUsedAt: string | null;
    useCount: number;
    gapCount: number;
    createdFromRunId?: string | null;
    playbookState?: string | null;
    playbookSuccessStreak?: number | null;
    actionsJson?: unknown[];
    compiledEntriesJson?: DeterministicCacheRowLike['compiledEntriesJson'];
    playbookJson?: PlaybookStep[] | null;
    compilationMeta?: Record<string, unknown> | null;
  }>;
};

type OptimizeResponse = {
  ok: boolean;
  error?: string;
  jobId?: string;
  runId?: string;
};

export type CachePlaybookPersistMode =
  | {
      mode: 'skip';
      reason: 'existing_row_healthy' | 'existing_row_proven' | 'existing_row_not_improved';
    }
  | { mode: 'patch' | 'upsert' | 'dry-run' };

type CachePlaybookResponse = {
  ok: boolean;
  error?: string;
  /** Server-side failure message with the full guard/validation context. */
  detail?: string;
  /** Actionable validation issues from the playbook builder. */
  issues?: string[];
  cacheId?: string;
  nodeHash?: string;
  cachedStepCount?: number;
  gapStepCount?: number;
  dryRun?: boolean;
  verified?: boolean;
  persistMode?: CachePlaybookPersistMode;
};

export function formatCachePlaybookCommitSuccess(
  result: Required<
    Pick<
      CachePlaybookResponse,
      'cacheId' | 'nodeHash' | 'cachedStepCount' | 'gapStepCount' | 'persistMode'
    >
  > & { verified?: boolean }
): string {
  const counts = `(${result.cachedStepCount} cached, ${result.gapStepCount} gap steps)`;
  const verification = result.verified ? '; imported as verified' : '';
  if (result.persistMode.mode === 'skip') {
    return (
      `Playbook unchanged: retained cache ${result.cacheId} under hash ${result.nodeHash} ` +
      `${counts}; server skipped persistence (${result.persistMode.reason})${verification}`
    );
  }
  if (result.persistMode.mode === 'patch') {
    return `Playbook patched: cache ${result.cacheId} under hash ${result.nodeHash} ${counts}${verification}`;
  }
  return `Playbook committed: cache ${result.cacheId} under hash ${result.nodeHash} ${counts}${verification}`;
}

export async function readCacheRecipeFile(recipePath: string): Promise<Record<string, unknown>> {
  let raw: string;
  try {
    raw = await fs.readFile(recipePath, 'utf-8');
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Could not read --recipe file: ${message}`);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Invalid JSON in --recipe file: ${message}`);
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Invalid --recipe file: expected a JSON object');
  }

  return parsed as Record<string, unknown>;
}

function printUsage(): void {
  console.error(
    [
      'Usage:',
      '  canary workflow cache status <workflowId> [--json]',
      '  canary workflow cache clear <workflowId> <mode> [--json]',
      '  canary workflow cache optimize <workflowId> [--run <runId>] [--json]',
      '  canary workflow cache optimize-dry --run <runId> [--workflow <workflowId>] [--out <dir>] [--json]',
      '  canary workflow cache substrate --workspace <dir> --action <idx> [--phase before|input|after]',
      '  canary workflow cache locator --workspace <dir> --action <idx> --ref <ref> [--var k=v ...]',
      '  canary workflow cache convergence-report <convergenceRunId> [--out <file.md>] [--json] [--refresh]',
      '  canary workflow cache commit <workflowId> --node <nodeId> --playbook <file.json> [--dry-run] [--json]  (--recipe: deprecated alias of --playbook)',
      '    [--force --expected-cache-id <uuid|none> --yes]',
      '    [--verified --verified-from <localRunId> --verified-from <localRunId>]',
      '    [--cache-only-boundary --yes]',
      '',
      'Cache clear modes (pick exactly one):',
      "  --node <nodeId>       Invalidate one node's cache row",
      '  --below-version <n>   Invalidate caches with compilerVersion < n',
      '  --stale-only          Invalidate caches whose playbookHealth = "stale"',
      '  --mode all            Invalidate every cache row (rows kept, isValid=false)',
      '  --mode hard           DESTRUCTIVE: delete cache rows, strip playbookSteps',
      '                        from node configs, and null lastRunId. Equivalent to',
      '                        the UI "Reset flow" button. Requires --yes.',
      '',
      'Options:',
      '  --yes                 Skip the interactive confirmation for --mode hard',
      '                        Required for cache commit --force/--verified',
      '  --json                Output raw JSON',
      '',
      'Convergence report options:',
      '  --out <file.md>       Write the markdown to a file instead of stdout',
      '  --refresh             Re-render and overwrite the stored report',
    ].join('\n')
  );
}

export async function handleWorkflowCache(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const [sub, ...rest] = argv;
  if (!sub || hasFlag(argv, '--help', '-h')) {
    printUsage();
    return;
  }

  switch (sub) {
    case 'status':
      await handleStatus(rest, apiUrl, token);
      return;
    case 'clear':
      await handleClear(rest, apiUrl, token, printUsage);
      return;
    case 'optimize':
      await handleOptimize(rest, apiUrl, token);
      return;
    case 'optimize-dry':
      await handleOptimizeDry(rest, apiUrl, token);
      return;
    case 'substrate':
      await handleSubstrate(rest);
      return;
    case 'locator':
      await handleLocator(rest);
      return;
    case 'convergence-report':
      await handleConvergenceReport(rest, apiUrl, token);
      return;
    case 'commit':
      await handleCommit(rest, apiUrl, token);
      return;
    default:
      console.error(`Unknown cache sub-command: ${sub}`);
      printUsage();
      process.exit(1);
  }
}

async function handleStatus(argv: string[], apiUrl: string, token: string): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Missing workflowId.');
    printUsage();
    process.exit(1);
  }
  const json = hasFlag(argv, '--json');

  const result = await apiRequest<CacheStatusResponse>(
    apiUrl,
    token,
    'GET',
    `/workflows/${workflowId}/cache-status`
  );

  if (!result.ok) {
    if (json) console.log(JSON.stringify(result, null, 2));
    else console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  const nodes = result.nodes ?? [];
  console.log(`Cache status for workflow ${workflowId} (${nodes.length} cache rows)\n`);
  if (nodes.length === 0) {
    console.log('  No caches.');
    return;
  }

  for (const n of nodes) {
    const health = n.playbookHealth ?? '—';
    const ver = n.compilerVersion ?? 'pre-v2';
    const gaps = n.gapCount;
    const validity = n.isValid === false ? 'invalid' : 'valid';
    const age = n.cachedAt ? new Date(n.cachedAt).toISOString().slice(0, 10) : '—';
    console.log(
      `  node=${n.nodeId.slice(0, 8)}  valid=${validity}  compilerVersion=${ver}  health=${health}  gaps=${gaps}  cachedAt=${age}  uses=${n.useCount}`
    );
  }
}

/**
 * Fetch a cache-convergence run's human review document.
 *
 * Replaces `scripts/render-convergence-report.ts`, which rendered the same
 * document from four raw SQL queries against a LOCAL database only —
 * reviewing a prod run first required copying it down with
 * `scripts/copy-convergence-run-to-local.ts`. Rendering now lives server-side
 * behind `GET /convergence-runs/:id/report`, so `--env prod` just works.
 */
async function handleConvergenceReport(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const runId = argv.find((a) => !a.startsWith('-'));
  if (!runId) {
    console.error('Missing convergenceRunId.');
    printUsage();
    process.exit(1);
  }
  const json = hasFlag(argv, '--json');
  const refresh = hasFlag(argv, '--refresh');
  const outPath = getArgValue(argv, '--out');

  const query = new URLSearchParams({ format: json ? 'json' : 'markdown' });
  if (refresh) query.set('refresh', '1');

  const result = await apiRequest<{
    ok: boolean;
    data?: { markdown?: string; cached?: boolean };
    error?: string;
  }>(apiUrl, token, 'GET', `/convergence-runs/${runId}/report?${query.toString()}`);

  if (!result.ok) {
    if (json) console.log(JSON.stringify(result, null, 2));
    else console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  const payload = json
    ? JSON.stringify(result.data ?? result, null, 2)
    : (result.data?.markdown ?? '');

  if (outPath) {
    await fs.writeFile(outPath, payload);
    console.error(
      `[convergence-report] wrote ${outPath}${result.data?.cached ? ' (stored report)' : ' (rendered now)'}`
    );
    return;
  }
  console.log(payload);
}

async function handleOptimize(argv: string[], apiUrl: string, token: string): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Missing workflowId.');
    printUsage();
    process.exit(1);
  }
  const json = hasFlag(argv, '--json');
  const runId = getArgValue(argv, '--run');

  const result = await apiRequest<OptimizeResponse>(
    apiUrl,
    token,
    'POST',
    `/workflows/${workflowId}/optimize-cache`,
    runId ? { runId } : {}
  );

  if (!result.ok) {
    if (json) console.log(JSON.stringify(result, null, 2));
    else console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  console.log(`Cache optimizer job enqueued: ${result.jobId} (harvesting run ${result.runId})`);
}

export interface LocalOptimizerQueueItem {
  nodeId: string;
  label: string;
  runStepId: string;
  runOrder: number;
  harvestReason: string;
  playbookSteps: PlaybookStep[];
  assertionSpecsByStepOrder?: Record<number, AssertionSpec>;
  runCodeSourcesByStepOrder?: Record<number, string>;
  runCodeToolCalls?: RunCodeToolCall[];
}

interface LocalSkippedOptimizerNode {
  nodeId: string;
  label: string;
  steps: number;
  skipReason: string;
}

interface LocalOptimizerQueueBuildResult {
  queue: LocalOptimizerQueueItem[];
  skipped: LocalSkippedOptimizerNode[];
}

async function handleOptimizeDry(argv: string[], apiUrl: string, token: string): Promise<void> {
  const parsed = parseOptimizeDryArgs(argv);
  if (!parsed.ok) {
    console.error(parsed.error);
    printUsage();
    process.exit(1);
  }

  try {
    const summary = await runOptimizeDry(parsed, apiUrl, token);
    if (parsed.json) {
      console.log(JSON.stringify(summary, null, 2));
    } else {
      console.log(formatOptimizeDrySummary(summary));
    }
  } catch (error) {
    if (parsed.json) {
      console.log(
        JSON.stringify(
          { ok: false, error: error instanceof Error ? error.message : String(error) },
          null,
          2
        )
      );
    } else {
      console.error(`Error: ${error instanceof Error ? error.message : String(error)}`);
    }
    process.exit(1);
  }
  // Leaked headless-browser handles can keep the process alive for hours after
  // the summary is written; the run is complete, so exit explicitly. Outside
  // the try/catch: the CLI's exit shim throws ProcessExitSignal, which must
  // propagate to the tracking wrapper, not land in the error handler above.
  process.exit(0);
}

async function runOptimizeDry(
  args: Extract<OptimizeDryParsedArgs, { ok: true }>,
  apiUrl: string,
  token: string
): Promise<OptimizeDrySummary> {
  const workspace = await ensureOptimizeDryWorkspace(args, apiUrl, token);
  const traceZipPath = path.join(workspace.workspace, 'browser', 'trace.zip');
  await assertReadableTrace(traceZipPath);

  const groups = parseTraceGroups(traceZipPath);
  const actions = parseTraceActions(traceZipPath);
  const traceContext = parseTraceContext(traceZipPath);
  const segments = segmentTraceByNode(groups, actions);
  const queueBuild = await buildLocalOptimizerQueue(workspace.workspace, workspace.runData);
  const queue = queueBuild.queue;
  const segmentNodeIds = resolveSegmentNodeIds(
    segments,
    workspace.runData.steps
      .slice()
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((step) => {
        const traceLabels = alternateTraceLabelsForStep(workspace.runData, step);
        return {
          nodeId: step.nodeId || null,
          label: traceLabelForStep(workspace.runData, step),
          ...(traceLabels ? { traceLabels } : {}),
          isSeedStep: isSeedRunStep(step),
        };
      })
  );
  await writeSegmentsFile(workspace.workspace, segments, segmentNodeIds);

  const provenance = collectLocalRunProvenance(workspace.runData);
  const requiredTokensByNode = buildRequiredTokensByNode(workspace.runData.nodes, queue);
  const statusResult = await fetchCacheStatusForDryRun(workspace.workflowId, apiUrl, token);
  const status = statusResult.status;
  const cacheRowsByNode = buildCacheRowsByNode(status);
  const session = new HeadlessDomSession();
  const materializer = new SubstrateMaterializer({
    traceZipPath,
    outDir: path.join(workspace.workspace, 'optimizer', 'deterministic-substrates'),
    session,
  });
  const verifier = new LocatorVerifier({ session });

  const nodes: OptimizeDryNodeSummary[] = queueBuild.skipped.map(skippedOptimizerNodeSummary);
  try {
    for (const item of queue) {
      const nodeSummary = await runOptimizeDryNode({
        item,
        workspaceRoot: workspace.workspace,
        traceZipPath,
        workflowId: workspace.workflowId,
        sourceRunId: workspace.runId,
        groups,
        segments,
        segmentNodeIds,
        viewport: traceContext.viewport,
        variableValues: provenance.variableValues,
        macroResolutionLog: provenance.macroResolutionLog,
        requiredTokens: requiredTokensByNode.get(item.nodeId),
        cacheRows: cacheRowsByNode.get(item.nodeId) ?? [],
        materializer,
        session,
        verifier,
      });
      nodes.push(nodeSummary);
    }
  } finally {
    await session.close();
  }

  const totals = buildOptimizeDryTotals(nodes);
  const rowBridge = summarizeRowBridge({
    queue,
    cacheRowsByNode,
    statusHasPayload: status.nodes?.some((node) => Array.isArray(node.actionsJson)) ?? false,
    ...(statusResult.error ? { fetchError: statusResult.error } : {}),
    runId: workspace.runId,
  });
  const summary: OptimizeDrySummary = {
    ok: true,
    runId: workspace.runId,
    workflowId: workspace.workflowId,
    workspace: workspace.workspace,
    rowBridge,
    totals,
    nodes,
  };
  await writeOptimizeDrySummary(workspace.workspace, summary);
  return summary;
}

async function buildLocalOptimizerQueue(
  workspaceRoot: string,
  runData: DryRunData
): Promise<LocalOptimizerQueueBuildResult> {
  const nodesById = new Map(runData.nodes.map((node) => [node.id, node]));
  const workspaceAssertions = await collectWorkspaceAssertionSpecs(workspaceRoot, runData);
  const thoughtCacheCodeByStep = await collectThoughtCacheCodeByStep(workspaceRoot);
  const timelineCacheCodeByStep = await collectTimelineCacheCodeByStep(workspaceRoot, runData);
  const queue: LocalOptimizerQueueItem[] = [];
  const skipped: LocalSkippedOptimizerNode[] = [];
  const queuedNodeIds = new Set<string>();
  const orderedSteps = runData.steps.slice().sort(compareStepsByActualRunOrder);
  for (const [runOrder, step] of orderedSteps.entries()) {
    if (step.status !== 'success' || !step.nodeId || queuedNodeIds.has(step.nodeId)) continue;
    const node = nodesById.get(step.nodeId);
    if (!node) continue;
    const harvestReason = isHarvestableStep(step);
    // Mirrors the server queue's source hierarchy (optimizer/queue.ts):
    // execution-time snapshot > configJson seed > synthesized.
    // configJson.playbookSteps is only the SEED playbook — the cache row owns
    // the live one — so the step's executedPlaybook (what this run actually
    // executed) always supersedes it; the seed covers steps that never ran
    // step-driven.
    const executedSnapshot = executedPlaybookSteps(step);
    const configuredPlaybookSteps =
      executedSnapshot.length === 0 ? (getConfiguredPlaybookSteps(node) ?? []) : [];
    const synthesized =
      executedSnapshot.length === 0 &&
      configuredPlaybookSteps.length === 0 &&
      node.nodeType === 'assertion'
        ? synthesizeAssertionVerifyPlaybook(step, workspaceAssertions.get(step.id))
        : undefined;
    const enriched = enrichPlaybookStepsWithRunCode({
      playbookSteps:
        executedSnapshot.length > 0
          ? executedSnapshot
          : configuredPlaybookSteps.length > 0
            ? configuredPlaybookSteps
            : (synthesized?.playbookSteps ?? []),
      runStep: step,
      ...(thoughtCacheCodeByStep.get(step.id)
        ? { thoughtCacheCode: thoughtCacheCodeByStep.get(step.id)! }
        : {}),
      timelineCacheCode: timelineCacheCodeByStep.get(step.id) ?? [],
    });
    const playbookSteps = enriched.playbookSteps;
    if (playbookSteps.length === 0) continue;
    if (!harvestReason) {
      skipped.push({
        nodeId: step.nodeId,
        label: traceLabelForStep(runData, step) || node.summary || step.nodeName || node.nodeType,
        steps: playbookSteps.length,
        skipReason: replayCleanSkipReason(step) ?? 'not-harvestable',
      });
      continue;
    }
    const fallbackAssertionSpecs = workspaceAssertions.get(step.id);
    const assertionSpecsByStepOrder =
      synthesized?.assertionSpecsByStepOrder ??
      assertionSpecsByVerifyStepOrder(playbookSteps, step, fallbackAssertionSpecs);
    const runCodeSourcesByStepOrder = {
      ...enriched.runCodeSourcesByStepOrder,
      ...assertionCodeSourcesByVerifyStepOrder(playbookSteps, step, fallbackAssertionSpecs),
    };
    const runCodeToolCalls = enriched.runCodeToolCalls;
    queuedNodeIds.add(step.nodeId);
    queue.push({
      nodeId: step.nodeId,
      label: traceLabelForStep(runData, step) || node.summary || step.nodeName || node.nodeType,
      runStepId: step.id,
      runOrder,
      harvestReason,
      playbookSteps,
      ...(assertionSpecsByStepOrder ? { assertionSpecsByStepOrder } : {}),
      ...(Object.keys(runCodeSourcesByStepOrder).length > 0 ? { runCodeSourcesByStepOrder } : {}),
      ...(runCodeToolCalls.length > 0 ? { runCodeToolCalls } : {}),
    });
  }
  return { queue, skipped };
}

function skippedOptimizerNodeSummary(skipped: LocalSkippedOptimizerNode): OptimizeDryNodeSummary {
  return {
    nodeId: skipped.nodeId,
    label: skipped.label,
    status: 'skipped',
    skipReason: skipped.skipReason,
    steps: skipped.steps,
    linkedByLayer: {},
    improved: 0,
    carried: 0,
    pending: 0,
    shadowCandidates: 0,
    shadowCandidateFailures: 0,
    gaps: {},
    rowBridge: 'unavailable',
  };
}

function replayCleanSkipReason(step: RunData['steps'][number]): string | null {
  const meta = isObject(step.metadataJson) ? step.metadataJson : {};
  const cacheOutcome = typeof meta.cacheOutcome === 'string' ? meta.cacheOutcome : null;
  const cacheHitSource = typeof meta.cacheHitSource === 'string' ? meta.cacheHitSource : null;
  if (cacheOutcome === 'hit' && cacheHitSource === 'replay') return 'replayed-clean';
  return null;
}

async function collectThoughtCacheCodeByStep(workspaceRoot: string): Promise<Map<string, string>> {
  const entries = await readJsonlFile(path.join(workspaceRoot, 'thoughts', 'all.jsonl'));
  const out = new Map<string, string>();
  for (const entry of entries) {
    if (!isObject(entry)) continue;
    if (entry.toolName !== 'browser_run_code') continue;
    const stepId = stringField(entry, 'stepId');
    if (!stepId || out.has(stepId)) continue;
    const code =
      stringField(entry, 'code') ??
      nestedStringField(entry, 'args', 'code') ??
      nestedStringField(entry, 'arguments', 'code') ??
      nestedStringField(entry, 'toolInput', 'code') ??
      nestedStringField(entry, 'input', 'code');
    if (code?.trim()) out.set(stepId, code);
  }
  return out;
}

async function collectTimelineCacheCodeByStep(
  workspaceRoot: string,
  runData: DryRunData
): Promise<Map<string, TimelineCacheCodeEvidence[]>> {
  const runStepByTraceLabel = new Map<string, RunData['steps'][number]>();
  for (const step of runData.steps) {
    const label = traceLabelForStep(runData, step);
    if (label && !runStepByTraceLabel.has(label)) runStepByTraceLabel.set(label, step);
  }

  const out = new Map<string, TimelineCacheCodeEvidence[]>();
  const entries = await readJsonlFile(path.join(workspaceRoot, 'timeline', 'actions.jsonl'));
  let currentStep: RunData['steps'][number] | null = null;
  for (const raw of entries) {
    if (!isObject(raw)) continue;
    if (raw.isAnnotation === true && raw.annotationType === 'step-started') {
      const label = stringField(raw, 'action');
      currentStep = label ? (runStepByTraceLabel.get(label) ?? null) : null;
      continue;
    }
    if (raw.isAnnotation === true && raw.annotationType === 'step-completed') {
      currentStep = null;
      continue;
    }
    if (!currentStep || raw.success !== true) continue;
    const action = stringField(raw, 'action');
    const code = action ? timelineActionCode(raw, action) : null;
    if (!action || !code) continue;
    const existing = out.get(currentStep.id) ?? [];
    existing.push({ action, code });
    out.set(currentStep.id, existing);
  }
  return out;
}

async function collectWorkspaceAssertionSpecs(
  workspaceRoot: string,
  runData: DryRunData
): Promise<Map<string, AssertionSpec[]>> {
  const actionSpecs = new Map<string, AssertionSpec[]>();
  const runStepByTraceLabel = new Map<string, RunData['steps'][number]>();
  for (const step of runData.steps) {
    const label = traceLabelForStep(runData, step);
    if (label && !runStepByTraceLabel.has(label)) runStepByTraceLabel.set(label, step);
  }
  const timelineActions = await readJsonlFile(
    path.join(workspaceRoot, 'timeline', 'actions.jsonl')
  );
  let currentStep: RunData['steps'][number] | null = null;
  for (const raw of timelineActions) {
    if (!isObject(raw)) continue;
    if (raw.isAnnotation === true && raw.annotationType === 'step-started') {
      const label = stringField(raw, 'action');
      currentStep = label ? (runStepByTraceLabel.get(label) ?? null) : null;
      continue;
    }
    if (raw.isAnnotation === true && raw.annotationType === 'step-completed') {
      currentStep = null;
      continue;
    }
    if (!currentStep || raw.action !== 'assert' || raw.success !== true) continue;
    const code = stringField(raw, 'value');
    const target = stringField(raw, 'target');
    const condition = code?.trim() || target?.trim();
    if (!condition) continue;
    const node = runData.nodes.find((candidate) => candidate.id === currentStep?.nodeId);
    appendAssertionSpec(actionSpecs, currentStep.id, {
      type: code?.trim() ? 'evaluate' : 'text_present',
      condition,
      severity: assertionSeverityFromNode(node) ?? 'warn',
      ...(target ? { label: target } : {}),
    });
  }

  const timelineAssertions = await readJsonlFile(
    path.join(workspaceRoot, 'timeline', 'assertions.jsonl')
  );
  for (const raw of timelineAssertions) {
    if (!isObject(raw)) continue;
    const stepId = stringField(raw, 'stepId');
    const condition = stringField(raw, 'condition');
    if (!stepId || !condition || raw.passed !== true) continue;
    if ((actionSpecs.get(stepId)?.length ?? 0) > 0) continue;
    const severity = assertionSeverity(raw.severity) ?? 'warn';
    appendAssertionSpec(actionSpecs, stepId, {
      type: 'text_present',
      condition,
      severity,
      ...(typeof raw.assertionId === 'string' ? { inlineAssertionId: raw.assertionId } : {}),
    });
  }

  return actionSpecs;
}

function appendAssertionSpec(
  target: Map<string, AssertionSpec[]>,
  stepId: string,
  spec: AssertionSpec
): void {
  const existing = target.get(stepId) ?? [];
  existing.push(spec);
  target.set(stepId, existing);
}

function assertionSeverity(value: unknown): AssertionSpec['severity'] | null {
  return value === 'warn' || value === 'fail' || value === 'fatal' ? value : null;
}

function assertionSeverityFromNode(
  node: RunData['nodes'][number] | undefined
): AssertionSpec['severity'] | null {
  if (!node || !isObject(node.configJson)) return null;
  return assertionSeverity(node.configJson.severity);
}

function stringField(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' ? value : null;
}

function nestedStringField(
  record: Record<string, unknown>,
  key: string,
  childKey: string
): string | null {
  const child = record[key];
  return isObject(child) ? stringField(child, childKey) : null;
}

function compareStepsByActualRunOrder(
  a: RunData['steps'][number],
  b: RunData['steps'][number]
): number {
  const aStarted = dateMillis(a.startedAt);
  const bStarted = dateMillis(b.startedAt);
  if (aStarted !== null && bStarted !== null && aStarted !== bStarted) return aStarted - bStarted;
  if (aStarted !== null && bStarted === null) return -1;
  if (aStarted === null && bStarted !== null) return 1;

  const aFinished = dateMillis(a.finishedAt);
  const bFinished = dateMillis(b.finishedAt);
  if (aFinished !== null && bFinished !== null && aFinished !== bFinished)
    return aFinished - bFinished;
  if (aFinished !== null && bFinished === null) return -1;
  if (aFinished === null && bFinished !== null) return 1;

  if (a.orderIndex !== b.orderIndex) return a.orderIndex - b.orderIndex;
  return a.id.localeCompare(b.id);
}

function dateMillis(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

function getConfiguredPlaybookSteps(node: RunData['nodes'][number]): PlaybookStep[] | undefined {
  if (node.nodeType !== 'action' && node.nodeType !== 'assertion') return undefined;
  if (!isObject(node.configJson)) return undefined;
  const raw = node.configJson.playbookSteps;
  return isPlaybookStepArray(raw) ? raw : undefined;
}

function isPlaybookStepArray(value: unknown): value is PlaybookStep[] {
  if (!Array.isArray(value)) return false;
  return value.every((entry) => {
    if (!isObject(entry)) return false;
    return (
      typeof entry.order === 'number' &&
      typeof entry.action === 'string' &&
      typeof entry.target === 'string'
    );
  });
}

/**
 * Mirror of the server queue's executedPlaybook fallback (optimizer/queue.ts):
 * parse the run step's `metadataJson.executedPlaybook` snapshot into playbook
 * steps for playbook-v3 nodes whose playbook lives on the cache row. Values are
 * already re-tokenized to `$var.`/`$MACRO` form at persist time. Conservative:
 * any malformed entry discards the whole array.
 */
function executedPlaybookSteps(step: RunData['steps'][number]): PlaybookStep[] {
  const meta = isObject(step.metadataJson) ? step.metadataJson : {};
  const raw = meta.executedPlaybook;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  if (!isPlaybookStepArray(raw)) return [];
  return raw.map((entry) => ({
    order: entry.order,
    action: entry.action,
    target: entry.target,
    ...(isObject(entry.params) ? { params: entry.params as Record<string, string> } : {}),
    ...(typeof entry.note === 'string' ? { note: entry.note } : {}),
  }));
}

const CACHEABLE_ASSERTION_TYPES = new Set([
  'text_present',
  'text_absent',
  'element_visible',
  'element_hidden',
  'evaluate',
  'toast_present',
  'download_present',
  'download_content',
]);

function collectAssertionSpecs(
  step: RunData['steps'][number],
  fallbackSpecs: readonly AssertionSpec[] = []
): AssertionSpec[] {
  const meta = isObject(step.metadataJson) ? step.metadataJson : {};
  const rawResults = Array.isArray(meta.assertionResults) ? meta.assertionResults : [];
  const specs: AssertionSpec[] = [];
  for (const raw of rawResults) {
    if (!isObject(raw)) continue;
    if (raw.checked === false) continue;
    if (raw.passed !== true) continue;
    if (raw.source !== 'cache') continue;
    if (
      typeof raw.assertionType !== 'string' ||
      !CACHEABLE_ASSERTION_TYPES.has(raw.assertionType)
    ) {
      continue;
    }
    if (typeof raw.condition !== 'string' || !raw.condition.trim()) continue;
    if (raw.severity !== 'warn' && raw.severity !== 'fail' && raw.severity !== 'fatal') continue;
    specs.push({
      type: raw.assertionType as AssertionSpec['type'],
      condition: raw.condition,
      severity: raw.severity,
      ...(typeof raw.inlineAssertionId === 'string'
        ? { inlineAssertionId: raw.inlineAssertionId }
        : {}),
      ...(typeof raw.label === 'string' ? { label: raw.label } : {}),
    });
  }
  const deduped = new Map<string, AssertionSpec>();
  for (const spec of [...fallbackSpecs, ...specs]) {
    deduped.set(`${spec.type}:${spec.condition}:${spec.inlineAssertionId ?? ''}`, spec);
  }
  return [...deduped.values()];
}

function synthesizeAssertionVerifyPlaybook(
  step: RunData['steps'][number],
  fallbackSpecs?: readonly AssertionSpec[]
):
  | { playbookSteps: PlaybookStep[]; assertionSpecsByStepOrder: Record<number, AssertionSpec> }
  | undefined {
  const specs = collectAssertionSpecs(step, fallbackSpecs);
  if (specs.length === 0) return undefined;
  const assertionSpecsByStepOrder: Record<number, AssertionSpec> = {};
  const playbookSteps = specs.map((spec, index) => {
    const order = index + 1;
    assertionSpecsByStepOrder[order] = spec;
    return {
      order,
      action: 'verify',
      target: spec.condition || spec.label || 'assertion',
      params: {},
    } satisfies PlaybookStep;
  });
  return { playbookSteps, assertionSpecsByStepOrder };
}

function assertionSpecsByVerifyStepOrder(
  playbookSteps: readonly PlaybookStep[],
  step: RunData['steps'][number],
  fallbackSpecs?: readonly AssertionSpec[]
): Record<number, AssertionSpec> | undefined {
  const verifySteps = playbookSteps
    .filter((playbookStep) => playbookStep.action === 'verify')
    .sort((a, b) => a.order - b.order);
  if (verifySteps.length === 0) return undefined;
  const specs = collectAssertionSpecs(step, fallbackSpecs);
  if (specs.length === 0) return undefined;
  const byOrder: Record<number, AssertionSpec> = {};
  for (const [index, playbookStep] of verifySteps.entries()) {
    const spec = specs[index];
    if (!spec) break;
    byOrder[playbookStep.order] = spec;
  }
  return Object.keys(byOrder).length > 0 ? byOrder : undefined;
}

function assertionCodeSourcesByVerifyStepOrder(
  playbookSteps: readonly PlaybookStep[],
  step: RunData['steps'][number],
  fallbackSpecs: readonly AssertionSpec[] | undefined
): Record<number, string> {
  if (!fallbackSpecs?.some((spec) => spec.type === 'evaluate' || spec.type === 'playwright'))
    return {};
  if (collectAssertionSpecs(step).length > 0) return {};
  const verifySteps = playbookSteps
    .filter((playbookStep) => playbookStep.action === 'verify')
    .sort((a, b) => a.order - b.order);
  const out: Record<number, string> = {};
  for (const [index, playbookStep] of verifySteps.entries()) {
    if (fallbackSpecs[index]?.type === 'evaluate' || fallbackSpecs[index]?.type === 'playwright')
      out[playbookStep.order] = 'timeline-actions';
  }
  return out;
}

function isHarvestableStep(step: RunData['steps'][number]): string | null {
  const meta = isObject(step.metadataJson) ? step.metadataJson : {};
  const cacheOutcome = typeof meta.cacheOutcome === 'string' ? meta.cacheOutcome : null;
  const cacheHitSource = typeof meta.cacheHitSource === 'string' ? meta.cacheHitSource : null;
  if (cacheOutcome === 'miss' || cacheOutcome === 'fallback') {
    return `cache ${cacheOutcome} - agent executed live`;
  }
  if (cacheHitSource === 'recovery' || cacheHitSource === 'l2_gap') {
    return `replay needed ${cacheHitSource} healing`;
  }
  return null;
}

const DIAGNOSTIC_CONFIG_FIELDS = new Set([
  'agentNotes',
  'playbookRepairSource',
  'playbookRepairRunId',
  'playbookRepairUpdatedAt',
  'playbookStepsUpdatedAt',
]);

/**
 * Token enforcement must see the same playbook skeleton the queue chose for
 * each node (mirrors the server's requiredTokensByNode in optimizer/index.ts):
 * for a queued node that is the executedPlaybook-first selection, whose
 * `$var.`/`$MACRO` tokens are just as binding as seed ones. configJson remains
 * the fallback only for nodes the queue did not select a skeleton for.
 */
export function buildRequiredTokensByNode(
  nodes: RunData['nodes'],
  queue: readonly LocalOptimizerQueueItem[]
): Map<string, string[]> {
  const queuedStepsByNode = new Map(queue.map((item) => [item.nodeId, item.playbookSteps]));
  const out = new Map<string, string[]>();
  for (const node of nodes) {
    const tokens = collectTokens({
      playbookSteps: queuedStepsByNode.get(node.id) ?? getConfiguredPlaybookSteps(node) ?? [],
      config: executableNodeConfig(node.configJson),
    });
    if (tokens.size > 0) out.set(node.id, [...tokens]);
  }
  return out;
}

function executableNodeConfig(configJson: unknown): Record<string, unknown> {
  if (!isObject(configJson)) return {};
  return Object.fromEntries(
    Object.entries(configJson).filter(([key]) => !DIAGNOSTIC_CONFIG_FIELDS.has(key))
  );
}

async function fetchCacheStatus(
  workflowId: string,
  apiUrl: string,
  token: string
): Promise<CacheStatusResponse> {
  const result = await apiRequest<CacheStatusResponse>(
    apiUrl,
    token,
    'GET',
    `/workflows/${workflowId}/cache-status`
  );
  if (!result.ok) {
    throw new Error(result.error ?? 'cache-status request failed');
  }
  return result;
}

async function fetchCacheStatusForDryRun(
  workflowId: string,
  apiUrl: string,
  token: string
): Promise<{ status: CacheStatusResponse; error?: string }> {
  try {
    return { status: await fetchCacheStatus(workflowId, apiUrl, token) };
  } catch (error) {
    return {
      status: { ok: true, workflowId, nodes: [] },
      error: formatError(error),
    };
  }
}

function buildCacheRowsByNode(
  status: CacheStatusResponse
): Map<string, DeterministicCacheRowLike[]> {
  const out = new Map<string, DeterministicCacheRowLike[]>();
  for (const node of status.nodes ?? []) {
    if (!Array.isArray(node.actionsJson)) continue;
    out.set(node.nodeId, [
      {
        actionsJson: node.actionsJson,
        compiledEntriesJson: node.compiledEntriesJson ?? null,
        playbookJson: node.playbookJson ?? null,
        playbookSuccessStreak: node.playbookSuccessStreak ?? 0,
        createdFromRunId: node.createdFromRunId ?? null,
        compilationMeta: node.compilationMeta ?? null,
      } satisfies DeterministicCacheRowLike,
    ]);
  }
  return out;
}

async function runOptimizeDryNode(args: {
  item: LocalOptimizerQueueItem;
  workspaceRoot: string;
  traceZipPath: string;
  workflowId: string;
  sourceRunId: string;
  groups: ReturnType<typeof parseTraceGroups>;
  segments: NodeSegment[];
  segmentNodeIds: Array<string | null>;
  viewport?: TraceViewport;
  variableValues: Record<string, string>;
  macroResolutionLog: MacroResolutionLog;
  requiredTokens?: string[];
  cacheRows: readonly DeterministicCacheRowLike[];
  materializer: SubstrateMaterializer;
  session: HeadlessDomSession;
  verifier: LocatorVerifier;
}): Promise<OptimizeDryNodeSummary> {
  const prepared = prepareDeterministicNodeInputs({
    nodeId: args.item.nodeId,
    playbookSteps: args.item.playbookSteps,
    sourceRunId: args.sourceRunId,
    cacheRows: args.cacheRows,
    segments: args.segments,
    segmentNodeIds: args.segmentNodeIds,
  });
  const pure = optimizeNodeStepsPure({
    nodeId: args.item.nodeId,
    steps: args.item.playbookSteps,
    segmentActions: prepared.segmentActions,
    groups: args.groups,
    provenance: {
      variableValues: args.variableValues,
      macroResolutionLog: linkerMacroResolutionLog(args.macroResolutionLog),
    },
    ...(prepared.rowBridge ? { rowBridge: prepared.rowBridge } : {}),
    ...(prepared.parsedRunCodeBySteps.size > 0
      ? { parsedRunCodeBySteps: prepared.parsedRunCodeBySteps }
      : {}),
    ...(args.item.runCodeToolCalls && args.item.runCodeToolCalls.length > 0
      ? { runCodeToolCalls: args.item.runCodeToolCalls }
      : {}),
  });
  const resolvedLinkResult = await resolvePendingRowDisambiguationLocal({
    linkResult: pure.linkResult,
    segmentActions: prepared.segmentActions,
    rowBridgeRow: prepared.rowBridgeRow ?? prepared.incumbentRow,
    steps: args.item.playbookSteps,
    materializer: args.materializer,
    session: args.session,
    viewport: args.viewport,
    variableValues: args.variableValues,
  });
  const authored = await authorLinkedArtifacts({
    linkResult: resolvedLinkResult,
    segmentActions: prepared.segmentActions,
    steps: args.item.playbookSteps,
    cacheRow: prepared.rowBridgeRow ?? prepared.incumbentRow,
    parsedRunCodeBySteps: prepared.parsedRunCodeBySteps,
    materializer: args.materializer,
    session: args.session,
    verifier: args.verifier,
    viewport: args.viewport,
    variableValues: args.variableValues,
    incumbentActionsByStep: prepared.incumbentArtifacts,
  });
  const carryThrough = buildCarryThroughActions(args.item, resolvedLinkResult);
  let composition = composeDeterministicPlaybookPlan({
    nodeId: args.item.nodeId,
    playbookSteps: args.item.playbookSteps,
    candidateArtifactsByStep: authored.artifactsByStep,
    carryThroughActionsByStep: carryThrough,
    incumbentActionsByStep: prepared.incumbentArtifacts,
    incumbentQualitiesByStep: prepared.incumbentQualities,
    incumbentStreak: prepared.incumbentRow?.playbookSuccessStreak ?? 0,
    linkGaps: [...resolvedLinkResult.gaps, ...authored.gaps],
    pendingStepOrders: resolvedLinkResult.pendingRowDisambiguation.map(
      (pending) => pending.stepOrder
    ),
    linkSignalsByStep: linkSignalsByStepMap(resolvedLinkResult),
  });
  composition = await attachRunCodeCarryShadowCandidates({
    composition,
    linkResult: resolvedLinkResult,
    segmentActions: prepared.segmentActions,
    steps: args.item.playbookSteps,
    cacheRow: prepared.rowBridgeRow ?? prepared.incumbentRow,
    parsedRunCodeBySteps: prepared.parsedRunCodeBySteps,
    materializer: args.materializer,
    session: args.session,
    verifier: args.verifier,
    viewport: args.viewport,
    variableValues: args.variableValues,
    incumbentActionsByStep: prepared.incumbentArtifacts,
  });
  const decisionLog = stampRunCodeSources(
    stampLinkSources(composition.decisionLog, resolvedLinkResult.links),
    args.item.runCodeSourcesByStepOrder
  );
  await writeDecisionLog(args.workspaceRoot, decisionLog);

  const gaps = { ...decisionLog.linkStats.gapsByReason };
  try {
    buildCachePlaybook({
      nodeId: args.item.nodeId,
      ...composition.playbook,
      macroResolutionLog: args.macroResolutionLog,
      variableValues: args.variableValues,
      ...(args.requiredTokens ? { requiredTokens: args.requiredTokens } : {}),
    });
  } catch (error) {
    const key = error instanceof Error ? 'PLAYBOOK_VALIDATION' : 'PLAYBOOK_UNKNOWN';
    gaps[key] = (gaps[key] ?? 0) + 1;
  }

  return {
    nodeId: args.item.nodeId,
    label: args.item.label,
    status: 'evaluated',
    steps: args.item.playbookSteps.length,
    linkedByLayer: linkCountsByLayer(resolvedLinkResult.links),
    improved: composition.counts.improved,
    carried: composition.counts.incumbent + composition.counts.carry,
    pending: composition.counts.pending,
    shadowCandidates: composition.counts.shadowCandidates,
    shadowCandidateFailures: composition.counts.shadowCandidateFailures,
    gaps,
    rowBridge: prepared.rowBridgeRow ? 'available' : 'unavailable',
  };
}

function mergeResolvedPending(args: {
  linkResult: LinkResult;
  resolvedLinks: Array<{ stepOrder: number; actionIndex: number }>;
  gaps: Array<{ stepOrder: number; reason: LinkReasonCode; detail?: string }>;
}): LinkResult {
  const resolvedStepOrders = new Set(args.resolvedLinks.map((link) => link.stepOrder));
  return {
    links: [
      ...args.linkResult.links,
      ...args.resolvedLinks.map((link) => ({
        stepOrder: link.stepOrder,
        actionIndex: link.actionIndex,
        layer: 2 as const,
      })),
    ].sort((a, b) => a.stepOrder - b.stepOrder),
    carryThrough: args.linkResult.carryThrough,
    gaps: [...args.linkResult.gaps, ...args.gaps].sort((a, b) => a.stepOrder - b.stepOrder),
    pendingRowDisambiguation: args.linkResult.pendingRowDisambiguation.filter(
      (pending) => !resolvedStepOrders.has(pending.stepOrder)
    ),
    ...(args.linkResult.signalsByStep ? { signalsByStep: args.linkResult.signalsByStep } : {}),
  };
}

async function resolvePendingRowDisambiguationLocal(args: {
  linkResult: LinkResult;
  segmentActions: Array<{ index: number; action: TraceAction }>;
  rowBridgeRow: DeterministicCacheRowLike | null;
  steps: PlaybookStep[];
  materializer: SubstrateMaterializer;
  session: HeadlessDomSession;
  viewport?: TraceViewport;
  variableValues: Record<string, string>;
}): Promise<LinkResult> {
  if (args.linkResult.pendingRowDisambiguation.length === 0) return args.linkResult;
  const stepByOrder = new Map(args.steps.map((step) => [step.order, step]));
  const actionByIndex = new Map(args.segmentActions.map((entry) => [entry.index, entry.action]));
  const resolvedLinks: Array<{ stepOrder: number; actionIndex: number }> = [];
  const gaps: Array<{ stepOrder: number; reason: LinkReasonCode; detail?: string }> = [];
  for (const pending of args.linkResult.pendingRowDisambiguation) {
    const step = stepByOrder.get(pending.stepOrder);
    const strategy = rowStrategyForStep(args.rowBridgeRow, pending.stepOrder);
    if (!step || !strategy) {
      gaps.push({
        stepOrder: pending.stepOrder,
        reason: 'LINK_AMBIGUOUS_CANDIDATES',
        detail: 'no row strategy available for pending row disambiguation',
      });
      continue;
    }
    const candidateOutcomes: PendingRowCandidateOutcome[] = [];
    for (const actionIndex of pending.candidateActionIndices) {
      const action = actionByIndex.get(actionIndex);
      if (!action) {
        candidateOutcomes.push({
          actionIndex,
          status: 'inconclusive' as const,
          reason: 'ACTION_MISSING',
          detail: 'candidate action was not present in the segment action map',
        });
        continue;
      }
      const discovered = await discoverTarget({
        materializer: args.materializer,
        session: args.session,
        actionIndex,
        traceAction: traceActionWithPreview(action),
        primary: {
          kind: 'row-strategy',
          strategy,
          variableValues: args.variableValues,
        },
        viewport: args.viewport,
      }).catch((error: unknown) => ({
        ok: false as const,
        reason: 'GT_SIGNAL_CONFLICT' as const,
        detail: formatError(error),
      }));
      candidateOutcomes.push(classifyPendingRowCandidateDiscovery(actionIndex, discovered));
    }
    const candidateDecision = decidePendingRowCandidateOutcomes(
      candidateOutcomes,
      pending.candidateActionIndices.length
    );
    if (candidateDecision.ok) {
      resolvedLinks.push({ stepOrder: step.order, actionIndex: candidateDecision.actionIndex });
    } else {
      gaps.push({
        stepOrder: step.order,
        reason: 'LINK_AMBIGUOUS_CANDIDATES',
        detail: candidateDecision.detail,
      });
    }
  }
  return mergeResolvedPending({ linkResult: args.linkResult, resolvedLinks, gaps });
}

function stampRunCodeSources(
  log: NodeDecisionLog,
  sourcesByStepOrder: Record<number, string> | undefined
): NodeDecisionLog {
  if (!sourcesByStepOrder || Object.keys(sourcesByStepOrder).length === 0) return log;
  return {
    ...log,
    decisions: log.decisions.map((decision) => {
      const source = sourcesByStepOrder[decision.stepOrder];
      if (!source) return decision;
      const signal = `code_source:${source}`;
      const signals = decision.signals?.includes(signal)
        ? decision.signals
        : [...(decision.signals ?? []), signal];
      return { ...decision, signals };
    }),
  };
}

function stampLinkSources(log: NodeDecisionLog, links: LinkResult['links']): NodeDecisionLog {
  if (links.length === 0) return log;
  const linkByStep = new Map(links.map((link) => [link.stepOrder, link]));
  return {
    ...log,
    decisions: log.decisions.map((decision) => {
      const link = linkByStep.get(decision.stepOrder);
      if (!link) return decision;
      const signals = new Set(decision.signals ?? []);
      signals.add(`linked_layer:${link.layer}`);
      signals.add(`action:${link.actionIndex}`);
      return { ...decision, signals: [...signals] };
    }),
  };
}

function linkCountsByLayer(links: LinkResult['links']): Record<string, number> {
  const out: Record<string, number> = {};
  for (const link of links) {
    const key = String(link.layer);
    out[key] = (out[key] ?? 0) + 1;
  }
  return out;
}

async function writeDecisionLog(workspaceRoot: string, log: NodeDecisionLog): Promise<void> {
  const dir = path.join(workspaceRoot, 'optimizer', 'decisions');
  await fs.mkdir(dir, { recursive: true });
  await fs.writeFile(path.join(dir, `${log.nodeId}.json`), serializeNodeDecisionLog(log), 'utf-8');
}

async function writeSegmentsFile(
  workspaceRoot: string,
  segments: NodeSegment[],
  segmentNodeIds: Array<string | null>
): Promise<void> {
  const dir = path.join(workspaceRoot, 'optimizer');
  await fs.mkdir(dir, { recursive: true });
  await fs.writeFile(
    path.join(dir, 'segments.json'),
    JSON.stringify(
      segments.map((segment, index) => ({
        kind: segment.kind,
        stepNumber: segment.stepNumber,
        nodeId: segmentNodeIds[index] ?? null,
        label: segment.label,
        actionIndices: segment.actions.map((action) => action.index),
      })),
      null,
      2
    ),
    'utf-8'
  );
}

function summarizeRowBridge(args: {
  queue: LocalOptimizerQueueItem[];
  cacheRowsByNode: Map<string, DeterministicCacheRowLike[]>;
  statusHasPayload: boolean;
  fetchError?: string;
  runId: string;
}): OptimizeDrySummary['rowBridge'] {
  if (args.fetchError) {
    return {
      status: 'degraded',
      reason: `cache-status unavailable: ${args.fetchError}`,
    };
  }
  if (!args.statusHasPayload) {
    return {
      status: 'degraded',
      reason: 'cache-status did not include node cache row payloads',
    };
  }
  const missingSourceRows = args.queue.filter((item) => {
    const rows = args.cacheRowsByNode.get(item.nodeId) ?? [];
    return !rows.some((row) => row.createdFromRunId === args.runId);
  }).length;
  if (missingSourceRows > 0) {
    return {
      status: 'degraded',
      reason: `cache-status exposes latest valid rows; source-run row absent for ${missingSourceRows} node(s)`,
    };
  }
  return { status: 'full' };
}

async function writeOptimizeDrySummary(
  workspaceRoot: string,
  summary: OptimizeDrySummary
): Promise<void> {
  const dir = path.join(workspaceRoot, 'optimizer');
  await fs.mkdir(dir, { recursive: true });
  await fs.writeFile(
    path.join(dir, 'summary.json'),
    `${JSON.stringify(summary, null, 2)}\n`,
    'utf-8'
  );
}

function traceLabelForStep(runData: DryRunData, step: RunData['steps'][number]): string {
  const node = runData.nodes.find((candidate) => candidate.id === step.nodeId);
  if (!node) return step.nodeName ?? '';
  return deriveTraceLabelForNode(node) || step.nodeName || node.summary || node.nodeType;
}

function alternateTraceLabelsForStep(
  runData: DryRunData,
  step: RunData['steps'][number]
): readonly string[] | undefined {
  const labels = new Set<string>();
  if (step.nodeName) labels.add(step.nodeName);
  const node = runData.nodes.find((candidate) => candidate.id === step.nodeId);
  if (node?.summary) labels.add(node.summary);
  const primary = traceLabelForStep(runData, step);
  labels.delete(primary);
  return labels.size > 0 ? [...labels] : undefined;
}

function deriveTraceLabelForNode(node: RunData['nodes'][number]): string {
  const config = isObject(node.configJson) ? node.configJson : {};
  switch (node.nodeType) {
    case 'login':
      return stringField(config, 'credentialName') ?? 'Login';
    case 'navigate':
      return stringField(config, 'pageTitle') ?? stringField(config, 'pageUrl') ?? 'Navigate';
    case 'action':
      return (
        stringField(config, 'customActionTitle') ??
        stringField(config, 'actionDescription') ??
        stringField(config, 'actionKind') ??
        'Action'
      );
    case 'assertion':
      return stringField(config, 'condition') ?? 'Assertion';
    case 'setup':
      return stringField(config, 'setupFlowName')
        ? `Setup: ${stringField(config, 'setupFlowName')}`
        : 'Setup Flow';
    case 'wait':
      return 'Wait';
    case 'api_sequence':
      return stringField(config, 'description') ?? 'API Sequence';
    case 'sandbox_action':
      return (
        stringField(config, 'actionLabel') ?? stringField(config, 'actionSlug') ?? 'Sandbox Action'
      );
    case 'mcp':
      return stringField(config, 'instructions')?.split('\n')[0]?.trim() || 'MCP';
    default:
      return node.nodeType.charAt(0).toUpperCase() + node.nodeType.slice(1);
  }
}

function isSeedRunStep(step: RunData['steps'][number]): boolean {
  const meta = isObject(step.metadataJson) ? step.metadataJson : {};
  return meta.isSeedStep === true || typeof meta.seedWorkflowId === 'string';
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function formatError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Reconstruct one trace action's page state and capture its substrate-v2,
 * entirely locally against a materialized run workspace
 * (`canary workflow debug materialize <runId> --out <dir>`).
 */
async function handleSubstrate(argv: string[]): Promise<void> {
  const workspace = getArgValue(argv, '--workspace');
  const actionIndexRaw = getArgValue(argv, '--action');
  const phase = parseTraceSnapshotPhase(getArgValue(argv, '--phase') ?? 'before');
  if (!workspace || actionIndexRaw === undefined) {
    console.error(
      'Usage: canary workflow cache substrate --workspace <dir> --action <idx> [--phase before|input|after]  (idx = position in browser/actions.jsonl / segments.json)'
    );
    process.exit(1);
  }
  const actionIndex = Number(actionIndexRaw);
  const { SubstrateMaterializer } = await import('@chatsdet/workflow-cache');
  const path = await import('node:path');
  const materializer = new SubstrateMaterializer({
    traceZipPath: path.join(workspace, 'browser', 'trace.zip'),
    outDir: path.join(workspace, 'optimizer', 'substrates'),
  });
  try {
    const result = await materializer.materialize(actionIndex, phase);
    console.log(
      JSON.stringify({ substratePath: result.substratePath, domPath: result.domPath }, null, 2)
    );
  } finally {
    await materializer.close();
  }
}

/**
 * Derive a deterministic locator for an element (by aria-ref) and verify it
 * offline against the trace-reconstructed DOM. Locally, no API.
 */
async function handleLocator(argv: string[]): Promise<void> {
  const workspace = getArgValue(argv, '--workspace');
  const actionIndexRaw = getArgValue(argv, '--action');
  const ref = getArgValue(argv, '--ref');
  const phase = parseTraceSnapshotPhase(getArgValue(argv, '--phase') ?? 'before');
  if (!workspace || actionIndexRaw === undefined || !ref) {
    console.error(
      'Usage: canary workflow cache locator --workspace <dir> --action <idx> --ref <ref> [--var name=value ...]  (idx = position in browser/actions.jsonl / segments.json)'
    );
    process.exit(1);
  }
  const variableValues: Record<string, string> = {};
  for (let i = 0; i < argv.length - 1; i++) {
    if (argv[i] === '--var') {
      const [name, ...valueParts] = (argv[i + 1] ?? '').split('=');
      if (name && valueParts.length > 0) variableValues[name] = valueParts.join('=');
    }
  }
  const actionIndex = Number(actionIndexRaw);
  const { HeadlessDomSession, SubstrateMaterializer, LocatorVerifier, buildLocator } =
    await import('@chatsdet/workflow-cache');
  const { parseSnapshot } = await import('@chatsdet/browser-core');
  const path = await import('node:path');
  // One Chromium for capture + verification — they must share a rendering
  // environment or the aria-ref table shifts between the two.
  const session = new HeadlessDomSession();
  const materializer = new SubstrateMaterializer({
    traceZipPath: path.join(workspace, 'browser', 'trace.zip'),
    outDir: path.join(workspace, 'optimizer', 'substrates'),
    session,
  });
  const verifier = new LocatorVerifier({ session });
  try {
    const substrate = await materializer.materialize(actionIndex, phase);
    const snapshot = parseSnapshot(substrate.substrateYaml);
    const built = buildLocator(snapshot, ref, variableValues);
    if (built.irreducible) {
      console.log(
        JSON.stringify(
          { verified: false, irreducible: true, reason: 'no unique predicate — mark as gap' },
          null,
          2
        )
      );
      process.exit(2);
    }
    const verification = await verifier.verify({
      domPath: substrate.domPath,
      targetRef: ref,
      strategy: built.selectors,
      variableValues,
    });
    console.log(
      JSON.stringify(
        {
          verified: verification.verdict,
          predicate: built.predicate,
          selectors: built.selectors,
          verification,
        },
        null,
        2
      )
    );
    if (!verification.verdict) process.exit(2);
  } finally {
    await session.close();
  }
}

function parseTraceSnapshotPhase(value: string): TraceSnapshotPhase {
  if (value === 'before' || value === 'input' || value === 'after') return value;
  throw new Error(`Invalid phase '${value}' (expected before, input, or after)`);
}

/** Commit an optimizer-authored playbook through the server-validated endpoint. */
async function handleCommit(argv: string[], apiUrl: string, token: string): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  const nodeId = getArgValue(argv, '--node');
  // --recipe is a deprecated alias of --playbook (2026-08 recipe→playbook rename).
  const playbookPath = getArgValue(argv, '--playbook') ?? getArgValue(argv, '--recipe');
  const json = hasFlag(argv, '--json');
  const dryRun = hasFlag(argv, '--dry-run');
  const force = hasFlag(argv, '--force');
  const verified = hasFlag(argv, '--verified');
  const cacheOnlyBoundaryAuthoring =
    hasFlag(argv, '--cache-only-boundary') || hasFlag(argv, '--unverified-from-trace');
  const yes = hasFlag(argv, '--yes');
  const expectedCacheIdRaw = getArgValue(argv, '--expected-cache-id');
  const verificationRunIds: string[] = [];
  for (let i = 0; i < argv.length - 1; i++) {
    if (argv[i] === '--verified-from' && argv[i + 1]) verificationRunIds.push(argv[i + 1]!);
  }
  if (!workflowId || !nodeId || !playbookPath) {
    console.error(
      'Usage: canary workflow cache commit <workflowId> --node <nodeId> --playbook <file.json> [--dry-run] [--force --expected-cache-id <uuid|none> --yes] [--verified --verified-from <runId> --verified-from <runId>] [--unverified-from-trace --yes]'
    );
    process.exit(1);
  }
  if ((force || verified || cacheOnlyBoundaryAuthoring) && !yes) {
    console.error(
      'Error: --force, --verified, and unverified authoring require explicit --yes confirmation.'
    );
    process.exit(1);
  }
  if (
    cacheOnlyBoundaryAuthoring &&
    (force || verified || dryRun || expectedCacheIdRaw !== undefined)
  ) {
    console.error(
      'Error: unverified authoring cannot be combined with --force, --verified, --dry-run, or --expected-cache-id.'
    );
    process.exit(1);
  }
  if (verified && !force) {
    console.error('Error: --verified requires --force.');
    process.exit(1);
  }
  if (force && expectedCacheIdRaw === undefined) {
    console.error('Error: --force requires --expected-cache-id <uuid|none>.');
    process.exit(1);
  }
  if (verified && new Set(verificationRunIds).size < 2) {
    console.error('Error: --verified requires two distinct --verified-from local run IDs.');
    process.exit(1);
  }
  if (dryRun && (force || verified)) {
    console.error('Error: --dry-run cannot be combined with --force or --verified.');
    process.exit(1);
  }
  const playbook = await readCacheRecipeFile(playbookPath);
  const playbookPayload = { ...playbook };
  for (const operatorField of [
    'dryRun',
    'force',
    'confirmed',
    'expectedCacheId',
    'verified',
    'verificationRunIds',
    'cacheOnlyBoundaryAuthoring',
  ]) {
    delete playbookPayload[operatorField];
  }
  const promotion = force
    ? {
        force: true,
        confirmed: true,
        expectedCacheId: expectedCacheIdRaw === 'none' ? null : expectedCacheIdRaw,
        ...(verified
          ? { verified: true, verificationRunIds: [...new Set(verificationRunIds)] }
          : {}),
      }
    : {};
  const result = await apiRequest<CachePlaybookResponse>(
    apiUrl,
    token,
    'POST',
    `/workflows/${workflowId}/nodes/${nodeId}/cache-playbook`,
    dryRun
      ? { ...playbookPayload, dryRun: true }
      : {
          ...playbookPayload,
          ...promotion,
          ...(cacheOnlyBoundaryAuthoring
            ? { cacheOnlyBoundaryAuthoring: true, confirmed: true }
            : {}),
        }
  );
  if (!result.ok) {
    if (json) console.log(JSON.stringify(result, null, 2));
    else {
      console.error(`Error: ${result.error ?? 'Unknown error'}`);
      if (result.detail && result.detail !== result.error) console.error(`  ${result.detail}`);
      for (const issue of result.issues ?? []) console.error(`  - ${issue}`);
    }
    process.exit(1);
  }
  if (json) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }
  if (result.dryRun) {
    console.log(
      `Dry run OK: playbook validates and passes the commit guards ` +
        `(${result.cachedStepCount} cached, ${result.gapStepCount} gap steps; would mint under hash ${result.nodeHash})`
    );
    return;
  }
  console.log(
    formatCachePlaybookCommitSuccess({
      cacheId: result.cacheId!,
      nodeHash: result.nodeHash!,
      cachedStepCount: result.cachedStepCount!,
      gapStepCount: result.gapStepCount!,
      persistMode: result.persistMode ?? { mode: 'upsert' },
      verified: result.verified,
    })
  );
}
